import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getPosRequests, reviewPosRequest, connectPosTerminalAdmin } from "../../../redux/actions/profile";

const PRIMARY = "#a7cc48";
const BG = "#2c2d3f";

const TABS = ["requested", "processing", "delivered", "rejected"];

const NEXT_ACTION = {
  requested: { action: "process", label: "Start Processing" },
  processing: { action: "deliver", label: "Mark Delivered" },
};

export default function PosRequests() {
  const dispatch = useDispatch();
  const requests = useSelector((s) => s.profile.posRequests || []);
  const [status, setStatus] = useState("requested");
  const [busyId, setBusyId] = useState(null);
  const [rejectingId, setRejectingId] = useState(null);
  const [reason, setReason] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [connectForms, setConnectForms] = useState({});

  useEffect(() => { dispatch(getPosRequests(status)); }, [dispatch, status]);

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-NG", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

  const totalUnits = (r) => (r.branches || []).reduce((a, b) => a + (b.quantity || 1), 0);

  const handleAdvance = async (row) => {
    const next = NEXT_ACTION[row.request.status];
    if (!next) return;
    const key = row.requestId;
    setBusyId(key);
    const res = await dispatch(reviewPosRequest(row.userId?._id, row.requestId, next.action));
    setBusyId(null);
    if (res?.success) dispatch(getPosRequests(status));
    else alert(res?.message || "Unable to update request");
  };

  const setConnectField = (key, field, value) =>
    setConnectForms((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), [field]: value } }));

  // key identifies the form: `${requestId}:${branchIndex}` for a new terminal,
  // or `${requestId}:t:${terminalRecordId}` to fill a pending placeholder.
  const handleConnect = async (row, key, target) => {
    const form = connectForms[key] || {};
    if (!form.terminalId?.trim()) return alert("Enter the terminal ID");
    setBusyId(key);
    const res = await dispatch(
      connectPosTerminalAdmin(row.userId?._id, row.requestId, {
        ...target,
        terminalId: form.terminalId.trim(),
        serialNumber: (form.serialNumber || "").trim(),
      })
    );
    setBusyId(null);
    if (res?.success) {
      setConnectForms((prev) => ({ ...prev, [key]: {} }));
      dispatch(getPosRequests(status));
    } else alert(res?.message || "Unable to connect terminal");
  };

  const startReject = (key) => { setRejectingId(key); setReason(""); };
  const cancelReject = () => { setRejectingId(null); setReason(""); };

  const confirmReject = async (row) => {
    if (!reason.trim()) return alert("Please provide a rejection reason");
    const key = row.requestId;
    setBusyId(key);
    const res = await dispatch(reviewPosRequest(row.userId?._id, row.requestId, "reject", reason.trim()));
    setBusyId(null);
    if (res?.success) {
      setRejectingId(null);
      setReason("");
      dispatch(getPosRequests(status));
    } else alert(res?.message || "Unable to reject request");
  };

  return (
    <div style={S.root}>
      <div style={S.header}>
        <div>
          <h2 style={S.title}>SeerBit POS Terminal Requests</h2>
          <p style={S.sub}>Track and fulfil merchant orders for POS terminals</p>
        </div>
      </div>

      <div style={S.tabs}>
        {TABS.map((t) => (
          <button key={t} style={{ ...S.tab, ...(status === t ? S.tabActive : {}) }} onClick={() => setStatus(t)}>
            {t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      <div style={S.list}>
        {requests.length === 0 ? (
          <div style={S.empty}>No {status} POS requests</div>
        ) : requests.map((row) => {
          const key = row.requestId;
          const isExpanded = expandedId === key;
          const next = NEXT_ACTION[row.request.status];
          return (
            <div key={key} style={S.card}>
              <div style={S.cardHeader} onClick={() => setExpandedId(isExpanded ? null : key)}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, color: BG, fontSize: 14 }}>
                    {totalUnits(row.request)} terminal(s) · {(row.request.branches || []).length} branch(es)
                  </div>
                  <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>
                    {row.userId?.storeInfo?.storeName || "—"} · {row.userId?.email || "—"}
                  </div>
                </div>
                <span style={{ ...S.statusPill, ...statusStyle(row.request.status) }}>{row.request.status}</span>
                <i className={`bi bi-chevron-${isExpanded ? "up" : "down"}`} style={{ color: "#888", marginLeft: 12 }} />
              </div>

              {isExpanded && (
                <div style={S.cardBody}>
                  <div style={S.branchList}>
                    {(row.request.branches || []).map((b, i) => (
                      <div key={i} style={S.branchRow}>
                        <span style={{ fontWeight: 700, color: BG }}>{b.quantity}×</span> {b.branch} — {b.address}
                        <div style={{ fontSize: 12, color: "#888" }}>
                          Recipient: {b.recipientName || "—"} · {b.recipientPhone || "—"}
                        </div>
                        {row.request.status !== "rejected" && row.request.status !== "delivered" && (
                          <div style={S.connectRow}>
                            <input
                              style={S.smallInput}
                              placeholder="Terminal ID"
                              value={connectForms[`${key}:${i}`]?.terminalId || ""}
                              onChange={(e) => setConnectField(`${key}:${i}`, "terminalId", e.target.value)}
                            />
                            <input
                              style={S.smallInput}
                              placeholder="Serial no."
                              value={connectForms[`${key}:${i}`]?.serialNumber || ""}
                              onChange={(e) => setConnectField(`${key}:${i}`, "serialNumber", e.target.value)}
                            />
                            <button
                              style={S.approveBtn}
                              disabled={busyId === `${key}:${i}`}
                              onClick={() => handleConnect(row, `${key}:${i}`, { branchIndex: i })}
                            >
                              Connect terminal
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {(row.terminals || []).length > 0 && (
                    <div style={{ marginBottom: 14 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: BG, marginBottom: 6 }}>Terminals</div>
                      {row.terminals.map((t) => {
                        const tkey = `${key}:t:${t._id}`;
                        return (
                          <div key={t._id} style={S.branchRow}>
                            {t.label} — {t.status === "pending" ? (
                              <span style={S.connectRow}>
                                <input
                                  style={S.smallInput}
                                  placeholder="Terminal ID"
                                  value={connectForms[tkey]?.terminalId || ""}
                                  onChange={(e) => setConnectField(tkey, "terminalId", e.target.value)}
                                />
                                <input
                                  style={S.smallInput}
                                  placeholder="Serial no."
                                  value={connectForms[tkey]?.serialNumber || ""}
                                  onChange={(e) => setConnectField(tkey, "serialNumber", e.target.value)}
                                />
                                <button
                                  style={S.approveBtn}
                                  disabled={busyId === tkey}
                                  onClick={() => handleConnect(row, tkey, { terminalRecordId: t._id })}
                                >
                                  Connect
                                </button>
                              </span>
                            ) : (
                              <span>
                                {t.terminalId} {t.serialNumber ? `· S/N ${t.serialNumber}` : ""} · {t.status}
                                {t.connectedBy ? ` (by ${t.connectedBy})` : ""}
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  <div style={S.fieldsGrid}>
                    <Field label="Reason" value={row.request.reason} />
                    <Field label="Requested" value={fmtDate(row.request.requestedAt)} />
                    {row.request.processingAt && <Field label="Processing since" value={fmtDate(row.request.processingAt)} />}
                    {row.request.deliveredAt && <Field label="Delivered" value={fmtDate(row.request.deliveredAt)} />}
                  </div>

                  {row.request.status === "rejected" && row.request.rejectionReason && (
                    <div style={S.rejectionNotice}>
                      <strong>Rejection reason:</strong> {row.request.rejectionReason}
                    </div>
                  )}

                  {(next || row.request.status !== "rejected") && row.request.status !== "delivered" && (
                    <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
                      {next && (
                        <button style={S.approveBtn} disabled={busyId === key} onClick={() => handleAdvance(row)}>
                          <i className="bi bi-arrow-right-circle" style={{ marginRight: 4 }} />{next.label}
                        </button>
                      )}
                      <button style={S.rejectBtn} disabled={busyId === key} onClick={() => startReject(key)}>
                        <i className="bi bi-x-lg" style={{ marginRight: 4 }} />Reject
                      </button>
                    </div>
                  )}

                  {rejectingId === key && (
                    <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginTop: 12 }}>
                      <input
                        style={S.reasonInput}
                        placeholder="Reason for rejection (sent to the merchant by email)…"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        autoFocus
                      />
                      <button style={S.rejectBtn} disabled={busyId === key} onClick={() => confirmReject(row)}>Confirm Reject</button>
                      <button style={S.cancelBtn} onClick={cancelReject}>Cancel</button>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const Field = ({ label, value }) => (
  <div>
    <div style={{ fontSize: 11, color: "#999" }}>{label}</div>
    <div style={{ fontSize: 13, color: BG, fontWeight: 500 }}>{value || "—"}</div>
  </div>
);

const statusStyle = (status) => {
  if (status === "delivered") return { background: "rgba(167,204,72,0.12)", color: "#5a8a00" };
  if (status === "rejected") return { background: "rgba(230,57,70,0.1)", color: "#e63946" };
  if (status === "processing") return { background: "rgba(244,162,97,0.15)", color: "#c1741f" };
  return { background: "rgba(79,176,198,0.12)", color: "#2f7a8c" };
};

const S = {
  root: { paddingBottom: 40 },
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12, marginBottom: 18 },
  title: { fontSize: 22, fontWeight: 800, color: BG, margin: 0 },
  sub: { fontSize: 13, color: "#888", marginTop: 4 },
  tabs: { display: "flex", gap: 6, marginBottom: 18 },
  tab: { padding: "7px 14px", borderRadius: 20, border: "1.5px solid #e0e0e0", background: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer", color: "#888" },
  tabActive: { background: PRIMARY, borderColor: PRIMARY, color: "#fff" },
  list: { display: "flex", flexDirection: "column", gap: 12 },
  empty: { background: "#fff", borderRadius: 10, padding: 32, textAlign: "center", color: "#bbb", boxShadow: "0 2px 10px rgba(0,0,0,0.06)" },
  card: { background: "#fff", borderRadius: 10, boxShadow: "0 2px 10px rgba(0,0,0,0.06)", overflow: "hidden" },
  cardHeader: { display: "flex", alignItems: "center", padding: "14px 18px", cursor: "pointer" },
  cardBody: { padding: "0 18px 18px", borderTop: "1px solid #f5f5f5", paddingTop: 16 },
  branchList: { display: "flex", flexDirection: "column", gap: 4, marginBottom: 14 },
  branchRow: { fontSize: 13, color: "#555" },
  fieldsGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 14 },
  statusPill: { borderRadius: 20, padding: "3px 12px", fontSize: 11, fontWeight: 700 },
  rejectionNotice: { marginTop: 14, padding: "10px 14px", background: "rgba(230,57,70,0.06)", borderRadius: 8, fontSize: 12, color: "#e63946" },
  approveBtn: { background: "rgba(167,204,72,0.12)", border: "1px solid " + PRIMARY, borderRadius: 6, color: "#5a8a00", padding: "6px 14px", cursor: "pointer", fontSize: 12, fontWeight: 600 },
  rejectBtn: { background: "rgba(230,57,70,0.08)", border: "1px solid #e63946", borderRadius: 6, color: "#e63946", padding: "6px 14px", cursor: "pointer", fontSize: 12, fontWeight: 600 },
  cancelBtn: { background: "#fff", border: "1px solid #e0e0e0", borderRadius: 6, color: "#888", padding: "6px 14px", cursor: "pointer", fontSize: 12, fontWeight: 600 },
  connectRow: { display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginTop: 6 },
  smallInput: { padding: "5px 10px", borderRadius: 6, border: "1.5px solid #e0e0e0", fontSize: 12, width: 140 },
  reasonInput: { flex: 1, minWidth: 260, padding: "7px 12px", borderRadius: 8, border: "1.5px solid #e0e0e0", fontSize: 13, outline: "none" },
};
